import { Info } from "@mui/icons-material";
import { Alert, Box, IconButton, Snackbar as MuiSnackbar, Typography } from "@mui/material";
import * as React from "react";
import { useLocation } from "react-router-dom";
import Animation from "../animation";
import animationData from "../../assets/lottie/success.json";
import Dialog from "../dialog/dialog";
import Button from "../button/button";
import CloseIcon from "../../assets/icon/close";

interface SnackbarProps {
  handleClose: () => void;
  open: boolean;
  message: string;
  type: string;
  mobile?: boolean;
  isDialog?: boolean;
  content?: React.ReactNode;
  customWidth?: string;
}

const Snackbar: React.FC<SnackbarProps> = ({
  handleClose,
  open,
  message,
  type,
  mobile = false,
  isDialog = false,
  content,
  customWidth,
}) => {
  // const { handleClose, open, message, type, mobile, isDialog = false } = props;
  const [openDialog, setOpenDialog] = React.useState<boolean>(true);
  const location = useLocation();

  // Check if we're on /setting route or its child routes
  const isSettingRoute = location.pathname.startsWith("/setting");

  const backgroundColor = () => {
    if (type === "error") return "#FFEBE6";
    else return "#F0FDF4";
  };

  const renderContent = React.useMemo(() => {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "32px", width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Animation
            animationData={animationData}
            autoplay
            loop={false}
            style={{
              height: 80,
              width: 80,
            }}
          />
          <Typography sx={{ textAlign: "center", fontSize: "14px", fontWeight: "600" }}>
            Success
          </Typography>

          {content ?? (
            <Typography
              sx={{ textAlign: "center", fontSize: "12px", fontWeight: "400", width: "100%" }}
            >
              {message && message.toLowerCase() !== "success"
                ? message
                : "Data saved successfully."}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setOpenDialog(false);
            handleClose();
          }}
        >
          Got It
        </Button>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            padding: "0",
            position: "absolute",
            right: "16px",
            top: "16px",
          }}
        >
          <CloseIcon sx={{ width: "16px", height: "16px" }} />
        </IconButton>
      </Box>
    );
  }, [content, handleClose, message]);

  React.useEffect(() => {
    if (open && type !== "error") setOpenDialog(true);
  }, [open, type]);

  if ((isSettingRoute || isDialog) && open && type !== "error")
    return (
      <Dialog
        maxWidth={"xs"}
        // smallContent
        customWidth={customWidth ?? "300px"}
        open={openDialog}
        handleClose={() => {
          handleClose();
          setOpenDialog(false);
        }}
        renderContent={renderContent}
      />
    );

  return (
    <MuiSnackbar
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      autoHideDuration={5000}
      onClose={handleClose}
      open={open}
      sx={{
        bottom: mobile ? "10%" : "",
        position: "fixed",
        alignItems: mobile ? "baseline" : "",
      }}
    >
      <Alert
        icon={
          type === "error" ? (
            <Info color="error" />
          ) : (
            <Animation
              animationData={animationData}
              autoplay
              loop={false}
              style={{
                height: 40,
                width: 40,
              }}
            />
          )
        }
        onClose={handleClose}
        sx={{
          backgroundColor: backgroundColor(),
          color: type === "error" ? "#EB1212" : "#008236",
          width: "100%",
          border: `1px solid ${type === "error" ? "#EB1212" : "#008236"}`,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          fontSize: "14px",
          fontWeight: "600",
        }}
      >
        {message}
      </Alert>
    </MuiSnackbar>
  );
};

export default Snackbar;
